import { useTicketInformation } from '@/src/listings/hooks/useTicketInformation/useTicketInformation';
import { useIsEventbritePlusEnabled } from '@/src/listings/bsp/config';
import { useUserSubscription } from '@/src/listings/hooks/useUserSubscription';
import { useDisplayStatus } from '@/src/listings/components/_shared/contexts/DisplayStatusContext';
import { HighLevelStatus } from '@/src/listings/utils/highLevelStatus';

// Matches TEPS enforcement, which opens Early Access at `sales_start - 2h`.
const EARLY_ACCESS_WINDOW_MS = 2 * 60 * 60 * 1000;

export type EarlyAccessPhase = 'none' | 'announcement' | 'open';

export type EarlyAccessState = {
  // `announcement`: entitled member, window still >2h away. `open`: inside the 2h window.
  phase: EarlyAccessPhase;
  // Moment the Early Access window opens (public start - 2h), when known.
  windowOpenIso?: string;
};

// Viewer-and-window Early Access state for the conversion bar; display only, TEPS stays the boundary.
// The phase is resolved from ticket-information at render, so a tab left open across a boundary keeps
// the phase until the next load/refetch — accepted, the user refreshes when sales start.
export const useEarlyAccessState = (): EarlyAccessState => {
  const { displayStatus } = useDisplayStatus();
  const [ticketInfo] = useTicketInformation();
  const { isEnabled: isEventbritePlusEnabled } = useIsEventbritePlusEnabled();
  const { isSubscribed, hasEarlyAccessPurchaseWindows, isSubscriptionReady } = useUserSubscription();

  const parsedStart = ticketInfo?.earlyAccess?.nextSalesStartDate
    ? Date.parse(ticketInfo.earlyAccess.nextSalesStartDate)
    : NaN;
  const hasStart = Number.isFinite(parsedStart);
  const windowOpenIso = hasStart ? new Date(parsedStart - EARLY_ACCESS_WINDOW_MS).toISOString() : undefined;

  const isEligible =
    displayStatus === HighLevelStatus.SALES_START_SOON &&
    (isEventbritePlusEnabled || isSubscribed) &&
    isSubscriptionReady &&
    hasEarlyAccessPurchaseWindows &&
    ticketInfo?.earlyAccess?.hasEarlyAccessTickets === true;

  if (!isEligible) {
    return { phase: 'none' };
  }

  if (ticketInfo?.earlyAccess?.isPurchaseWindowOpen === true) {
    return { phase: 'open', windowOpenIso };
  }

  // The announcement names the window-open time, so it needs a concrete future start.
  return hasStart ? { phase: 'announcement', windowOpenIso } : { phase: 'none' };
};
